// @ts-nocheck
import type { ExpressionDefinition } from '../../types/script';
import { collectExpressionRefsFromAstRoot } from './collectExpressionRefs';

export interface ExpressionCycleIssue {
  expressionIds: string[];
  message: string;
}

function buildAdjacency(
  expressions: readonly ExpressionDefinition[],
): Map<string, string[]> {
  const adjacency = new Map<string, string[]>();
  for (const expression of expressions) {
    const refs = collectExpressionRefsFromAstRoot(expression.root as Record<string, unknown>);
    adjacency.set(expression.id, refs);
  }
  return adjacency;
}

function formatCycle(ids: string[]): string {
  return ids.join(' → ');
}

export function detectExpressionCycles(
  expressions: readonly ExpressionDefinition[],
): ExpressionCycleIssue[] {
  const adjacency = buildAdjacency(expressions);
  const visiting = new Set<string>();
  const visited = new Set<string>();
  const stack: string[] = [];
  const issues: ExpressionCycleIssue[] = [];
  const reported = new Set<string>();

  function reportCycle(cycleStartIndex: number): void {
    const cycleIds = stack.slice(cycleStartIndex);
    cycleIds.push(cycleIds[0]!);
    const key = [...cycleIds].sort().join('|');
    if (reported.has(key)) {
      return;
    }
    reported.add(key);
    issues.push({
      expressionIds: cycleIds.slice(0, -1),
      message: `Expression dependency cycle detected: ${formatCycle(cycleIds)}.`,
    });
  }

  function visit(id: string): void {
    if (visited.has(id)) {
      return;
    }
    if (visiting.has(id)) {
      const cycleStartIndex = stack.indexOf(id);
      if (cycleStartIndex >= 0) {
        reportCycle(cycleStartIndex);
      }
      return;
    }

    visiting.add(id);
    stack.push(id);
    for (const nextId of adjacency.get(id) ?? []) {
      visit(nextId);
    }
    stack.pop();
    visiting.delete(id);
    visited.add(id);
  }

  for (const expression of expressions) {
    visit(expression.id);
  }

  return issues;
}
